import { initializeApp, getApps, getApp } from 'firebase/app';
import { getDatabase } from 'firebase/database';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';

// DrawLab's own Firebase project (RTDB, Storage, Anonymous Auth)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// DDD Auth Hub Firebase project (for SSO across DDD apps)
// Custom tokens from auth-hub are signed against this project
const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_HUB_AUTH_DOMAIN || 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: import.meta.env.VITE_AUTH_HUB_PROJECT_ID || 'ddd-auth-hub-4ed14',
};

// Is Auth Hub integration enabled?
export const useAuthHub = import.meta.env.VITE_USE_AUTH_HUB === 'true';

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
export const database = getDatabase(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
// Callable functions live in asia-southeast1 (matches the RTDB/functions region)
export const functions = getFunctions(app, 'asia-southeast1');

// Auth Hub app (named instance — separate from main app)
export let authHubApp = null;
export let authHubAuth = null;
if (useAuthHub && authHubConfig.apiKey) {
  try {
    // Check if we already initialised it (hot reload safety)
    const existing = getApps().find((a) => a.name === 'auth-hub');
    authHubApp = existing || initializeApp(authHubConfig, 'auth-hub');
    authHubAuth = getAuth(authHubApp);
  } catch (err) {
    console.error('[firebase] Failed to initialise Auth Hub app:', err);
  }
}

// Initialise Analytics — only in supported browsers (async check)
let analyticsInstance = null;
isSupported().then((supported) => {
  if (supported && firebaseConfig.measurementId) {
    analyticsInstance = getAnalytics(app);
  }
}).catch(() => {
  // Analytics unsupported (e.g. SSR, some privacy modes) — silently ignore
});

/**
 * Log a custom analytics event. Safe to call before analytics has loaded —
 * events will be silently dropped until initialisation completes.
 */
export function trackEvent(eventName, params = {}) {
  if (analyticsInstance) {
    try {
      logEvent(analyticsInstance, eventName, params);
    } catch {
      // Ignore analytics errors — never break the app
    }
  }
}

/**
 * Ensure the user is signed in anonymously.
 * Returns the Firebase auth UID — used as the canonical user identity
 * instead of the old localStorage UUID.
 */
export async function ensureAnonymousAuth() {
  // If already signed in, return immediately
  if (auth.currentUser) {
    return auth.currentUser.uid;
  }

  // Wait for auth state to resolve (handles page refreshes where
  // Firebase restores the session from IndexedDB)
  const existingUser = await new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

  if (existingUser) {
    return existingUser.uid;
  }

  // No existing session — sign in anonymously
  const credential = await signInAnonymously(auth);
  return credential.user.uid;
}

export default app;
